import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { InformationCircleIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeSanitize from 'rehype-sanitize';
import { Modal } from 'src/components/DialogComponent';
import Loading from 'src/components/Loading';
import TGButton from 'src/components/ui/TGButton';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { FlexCol, FlexRow } from 'src/utils/flexLayouts';
import { addStrapiData, deleteStrapiData, extractStrapiData, fetch5SCardCopiedFrom, fetch5SInconsistencyFiles, fetchMt5SCardAudit, updateStrapiData } from '../api/api';
import { fetchData } from '../store/filtersData/actions';
import { selectModelData, selectModelLoading } from '../store/filtersData/selector';
import Inconsistency5SItemMobile, { createEmptyInconsistencyMobile, IncItemMobile } from './Inconsistency5SItem.mobile';
import PrevAuditInfoMobile, { PrevFile, PrevInc } from './PrevAuditInfo.mobile';

export interface PassAnswerPayloadMobile {
  Question: number;
  QuestionTitle: string | null;
  Answer: string | null;
  Comment: string | null;
}

export interface PassAudit5SMobileHandle {
  getAnswers: () => PassAnswerPayloadMobile[];
  saveAnswers: () => Promise<void>;
}

interface PassAudit5SMobileProps {
  card: any;
  onPercentChange?: (percent: number) => void;
  readOnly?: boolean;
  onOpenPrevAudit?: (cardId: number) => void;
}

interface AnswerState {
  id: number;
  documentId: string;
  answer: string;
  comment: string;
  inconsistencies: IncItemMobile[];
}

const ANSWER_OPTIONS = ['0', '5', '10', 'N/A'];

export const TEMPLATES_THIN_PARAMS_MOBILE = {
  'fields[0]': 'ChecklistType',
  'fields[1]': 'AuditType',
};
export const QUESTIONS_THIN_PARAMS_MOBILE = {
  'fields[0]': 'Title',
  'fields[1]': 'Section',
  'fields[2]': 'SortOrder',
  'fields[3]': 'AllowedAnswers',
  'fields[4]': 'Hide',
  'populate[Template][fields][0]': 'id',
};

const normalizeYo = (s: string | null | undefined) => (s ?? '').replace(/ё/gi, 'е');

function splitQuestionTitle(title: string | null | undefined): { text: string; hint: string } {
  const raw = (title || '').replace(/\r\n/g, '\n').trim();
  const textLines: string[] = [];
  const hintLines: string[] = [];
  raw.split('\n').forEach((line) => {
    if (/^\s*#{1,6}\s/.test(line)) textLines.push(line.replace(/^\s*#{1,6}\s*/, ''));
    else hintLines.push(line);
  });
  if (textLines.length === 0) return { text: raw, hint: '' };
  return { text: textLines.join('\n').trim(), hint: hintLines.join('\n').trim() };
}

async function fetchPrevIncFiles(incDocumentIds: string[]): Promise<Record<string, PrevFile[]>> {
  if (incDocumentIds.length === 0) return {};
  try {
    const resp = await fetch5SInconsistencyFiles(incDocumentIds);
    const rows = extractStrapiData(resp) || [];
    const byInc: Record<string, PrevFile[]> = {};
    for (const row of rows) {
      const incDoc = row.Inconsistency?.documentId;
      if (!incDoc) continue;
      if (!byInc[incDoc]) byInc[incDoc] = [];
      byInc[incDoc].push({
        name: row.File?.name || row.Title || 'file',
        sizeBytes: row.File?.size ? row.File.size * 1024 : 0,
        url: row.File?.url,
        fileId: row.File?.id,
      });
    }
    return byInc;
  } catch {
    return {};
  }
}

async function fetchPrevInconsistencies(prevCardId: number): Promise<Record<number, PrevInc[]>> {
  try {
    const resp = await fetchMt5SCardAudit(prevCardId);
    const data = resp?.data?.data || {};
    const answers: any[] = data.answers || [];
    const incs: any[] = data.inconsistencies || [];
    const qidByAnswerId: Record<number, number> = {};
    for (const a of answers) {
      const qid = a?.questionId;
      if (!qid) continue;
      qidByAnswerId[a.id] = qid;
    }
    const filesByIncDoc = await fetchPrevIncFiles(incs.map((i) => i.documentId).filter(Boolean));
    const incsByQ: Record<number, PrevInc[]> = {};
    for (const inc of incs) {
      const qid = qidByAnswerId[inc?.answerId];
      if (!qid) continue;
      if (!incsByQ[qid]) incsByQ[qid] = [];
      incsByQ[qid].push({
        id: inc.id,
        documentId: inc.documentId,
        Description: inc.Description || '',
        files: filesByIncDoc[inc.documentId] || [],
      });
    }
    return incsByQ;
  } catch {
    return {};
  }
}

function resolveChecklistType(card: any, depends: any[]): string | null {
  if (!card || !depends?.length) return null;
  const normalizedCardType = normalizeYo(card.TypeAudit);
  const match = depends.find((dep: any) => {
    const sameType = normalizeYo(dep.TypeAudit) === normalizedCardType;
    const sameLocation = dep.Location?.id === card.Location?.id;
    const sameDept = dep.Department?.id === card.Department?.id;
    const sameArea = dep.Area?.id === card.Area?.id;
    const sameZone = !card.Zone || !dep.Zone || dep.Zone === card.Zone;
    return sameType && sameLocation && sameDept && sameArea && sameZone;
  });
  return match?.CheckList || null;
}

function getNumericAllowed(q: any): number[] {
  const raw: string[] = Array.isArray(q.AllowedAnswers) && q.AllowedAnswers.length > 0
    ? q.AllowedAnswers
    : ANSWER_OPTIONS;
  return raw.filter((a) => a !== 'N/A').map((a) => Number(a)).filter((n) => !Number.isNaN(n));
}

function calcPercent(questions: any[], answers: Record<number, AnswerState>): number {
  let sum = 0;
  let max = 0;
  for (const q of questions) {
    const ans = answers[q.id]?.answer;
    if (ans === 'N/A') continue;
    const numeric = getNumericAllowed(q);
    const localMax = numeric.length ? Math.max(...numeric) : 10;
    max += localMax;
    if (ans !== undefined && ans !== '') {
      const n = Number(ans);
      if (!Number.isNaN(n)) sum += n;
    }
  }
  if (max === 0) return 0;
  return Math.round((sum / max) * 100);
}

const PassAudit5SMobile = forwardRef<PassAudit5SMobileHandle, PassAudit5SMobileProps>(
  ({ card, onPercentChange, readOnly = false, onOpenPrevAudit }, ref) => {
    const dispatch = useAppDispatch();

    const templates = useAppSelector((state: any) => selectModelData(state, 'mt-5-s-templates'));
    const questions = useAppSelector((state: any) => selectModelData(state, 'mt-5-s-template-questions'));
    const depends = useAppSelector((state: any) => selectModelData(state, 'mt-5-s-depends'));
    const templatesLoading = useAppSelector((state: any) => selectModelLoading(state, 'mt-5-s-templates'));
    const questionsLoading = useAppSelector((state: any) => selectModelLoading(state, 'mt-5-s-template-questions'));

    const [answers, setAnswers] = useState<Record<number, AnswerState>>({});
    const [initializing, setInitializing] = useState(true);
    const initializedRef = useRef(false);

    const [prevAnswersMap, setPrevAnswersMap] = useState<Record<number, { answer: string; comment: string }>>({});
    const [prevIncsMap, setPrevIncsMap] = useState<Record<number, PrevInc[]>>({});
    const [copiedThisLoad, setCopiedThisLoad] = useState(false);
    const [copiedFromCardId, setCopiedFromCardId] = useState<number | null>(null);
    const [incsCopied, setIncsCopied] = useState<Record<number, boolean>>({});
    const [hint, setHint] = useState<string | null>(null);

    useEffect(() => {
      if (!templates.length) {
        dispatch(fetchData('mt-5-s-templates', TEMPLATES_THIN_PARAMS_MOBILE, { page: 1, pageSize: 100 }));
      }
      if (!questions.length) {
        dispatch(fetchData('mt-5-s-template-questions', QUESTIONS_THIN_PARAMS_MOBILE, { page: 1, pageSize: 500 }));
      }
      if (!depends.length) {
        dispatch(fetchData('mt-5-s-depends', {}, { page: 1, pageSize: 100000 }));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const checklistType = useMemo(() => resolveChecklistType(card, depends), [card, depends]);

    const template = useMemo(() => {
      if (card?.Template?.id) {
        return templates.find((t: any) => t.id === card.Template.id) || null;
      }
      const normalizedCardType = normalizeYo(card?.TypeAudit);
      return templates.find(
        (t: any) => t.ChecklistType === checklistType && normalizeYo(t.AuditType) === normalizedCardType
      ) || null;
    }, [templates, card, checklistType]);

    const templateQuestions = useMemo(() => {
      if (!template) return [];
      return questions
        .filter((q: any) => q.Template?.id === template.id)
        .sort((a: any, b: any) => (a.SortOrder || 0) - (b.SortOrder || 0));
    }, [questions, template]);

    const visibleQuestions = useMemo(
      () => templateQuestions.filter((q: any) => !q.Hide || answers[q.id]),
      [templateQuestions, answers]
    );

    const sections = useMemo(() => {
      const grouped: Record<string, any[]> = {};
      visibleQuestions.forEach((q: any) => {
        const section = q.Section || 'Без раздела';
        if (!grouped[section]) grouped[section] = [];
        grouped[section].push(q);
      });
      return grouped;
    }, [visibleQuestions]);

    useEffect(() => {
      if (!card?.id) return;
      if (!templateQuestions.length) return;
      if (initializedRef.current) return;
      initializedRef.current = true;

      let cancelled = false;

      (async () => {
        try {
          const auditResp = await fetchMt5SCardAudit(card.id);
          const auditData = auditResp?.data?.data || {};
          const existingAnswers: any[] = auditData.answers || [];
          const existingIncs: any[] = auditData.inconsistencies || [];

          const incsByAnswerId: Record<number, IncItemMobile[]> = {};
          const copiedByAnswerId: Record<number, boolean> = {};
          for (const inc of existingIncs) {
            const ansId = inc?.answerId;
            if (!ansId) continue;
            if (!incsByAnswerId[ansId]) incsByAnswerId[ansId] = [];
            incsByAnswerId[ansId].push({
              id: inc.id,
              documentId: inc.documentId,
              Description: inc.Description || '',
              FromPrev: !!inc.FromPrev,
            });
            if (inc.FromPrev) copiedByAnswerId[ansId] = true;
          }

          const answerByQuestionId: Record<number, any> = {};
          const prevAnswersByQ: Record<number, { answer: string; comment: string }> = {};
          const incsCopiedByQ: Record<number, boolean> = {};
          for (const a of existingAnswers) {
            const qid = a?.questionId;
            if (!qid) continue;
            answerByQuestionId[qid] = a;
            if (a.PrevAnswer) {
              prevAnswersByQ[qid] = { answer: a.PrevAnswer, comment: '' };
            }
            if (copiedByAnswerId[a.id]) {
              incsCopiedByQ[qid] = true;
            }
          }

          const initial: Record<number, AnswerState> = {};
          const toCreate: any[] = [];
          for (const q of templateQuestions) {
            const existing = answerByQuestionId[q.id];
            if (existing) {
              initial[q.id] = {
                id: existing.id,
                documentId: existing.documentId,
                answer: existing.Answer ?? '',
                comment: existing.Comment ?? '',
                inconsistencies: incsByAnswerId[existing.id] || [],
              };
            } else if (!q.Hide) {
              if (readOnly) {
                initial[q.id] = { id: 0, documentId: '', answer: '', comment: '', inconsistencies: [] };
              } else {
                toCreate.push(q);
              }
            }
          }

          if (toCreate.length > 0) {
            const created = await Promise.all(
              toCreate.map((q) =>
                addStrapiData('mt-5-s-card-answers', {
                  Cards: card.id,
                  Question: q.id,
                  QuestionTitle: q.Title || null,
                  Answer: null,
                  Comment: null,
                })
                  .then((resp) => ({ q, data: extractStrapiData(resp) }))
                  .catch(() => null)
              )
            );
            for (const item of created) {
              if (!item || !item.data) continue;
              initial[item.q.id] = {
                id: item.data.id,
                documentId: item.data.documentId,
                answer: '',
                comment: '',
                inconsistencies: [],
              };
            }
          }

          const copied = !!card?.CopiedFromCard || Object.keys(prevAnswersByQ).length > 0;

          if (!cancelled) {
            setPrevAnswersMap(prevAnswersByQ);
            setIncsCopied(incsCopiedByQ);
            setCopiedThisLoad(copied);
            setAnswers(initial);
          }

          let prevCardId: number | null = card?.CopiedFromCard?.id ?? null;
          if (copied && !prevCardId) {
            try {
              const cardResp = await fetch5SCardCopiedFrom(card.id);
              prevCardId = extractStrapiData(cardResp)?.[0]?.CopiedFromCard?.id ?? null;
            } catch {
              prevCardId = null;
            }
          }
          if (!cancelled) setCopiedFromCardId(prevCardId);

          if (prevCardId) {
            const prevMap = await fetchPrevInconsistencies(prevCardId);
            if (!cancelled) setPrevIncsMap(prevMap);
          }
        } catch (err) {
          console.error('Не удалось инициализировать ответы 5S', err);
        } finally {
          if (!cancelled) setInitializing(false);
        }
      })();

      return () => {
        cancelled = true;
      };
    }, [card?.id, templateQuestions]);

    const percent = useMemo(() => calcPercent(visibleQuestions, answers), [visibleQuestions, answers]);

    useEffect(() => {
      onPercentChange?.(percent);
    }, [percent, onPercentChange]);

    const setAnswer = (questionId: number, value: string) => {
      setAnswers((prev) => {
        const cur = prev[questionId];
        if (!cur) return prev;
        return { ...prev, [questionId]: { ...cur, answer: value } };
      });
    };

    const handleAddInconsistency = useCallback(async (questionId: number) => {
      const cur = answers[questionId];
      if (!cur) return;
      const created = await createEmptyInconsistencyMobile(cur.id);
      if (!created) return;
      setAnswers((prev) => {
        const c = prev[questionId];
        if (!c) return prev;
        return { ...prev, [questionId]: { ...c, inconsistencies: [...c.inconsistencies, created] } };
      });
    }, [answers]);

    const handleIncUpdated = useCallback((questionId: number, documentId: string, description: string) => {
      setAnswers((prev) => {
        const c = prev[questionId];
        if (!c) return prev;
        return {
          ...prev,
          [questionId]: {
            ...c,
            inconsistencies: c.inconsistencies.map((i) =>
              i.documentId === documentId ? { ...i, Description: description } : i
            ),
          },
        };
      });
    }, []);

    const handleCopyPrevInconsistencies = useCallback(async (questionId: number) => {
      const cur = answers[questionId];
      const prevCardId = card?.CopiedFromCard?.id ?? copiedFromCardId;
      if (!cur || !prevCardId) return;
      setIncsCopied((s) => ({ ...s, [questionId]: true }));

      let map = prevIncsMap;
      if (!map[questionId]) {
        map = await fetchPrevInconsistencies(prevCardId);
        setPrevIncsMap(map);
      }
      const prevIncs = map[questionId] || [];
      if (prevIncs.length === 0) return;

      const created = await Promise.all(
        prevIncs.map((p) =>
          addStrapiData('mt-5-s-inconsistencies', {
            Answer: cur.id,
            Description: p.Description,
            FromPrev: true,
          })
            .then((r) => extractStrapiData(r))
            .catch(() => null)
        )
      );
      const toAdd: IncItemMobile[] = created
        .filter(Boolean)
        .map((d: any) => ({
          id: d.id,
          documentId: d.documentId,
          Description: d.Description || '',
          FromPrev: true,
        }));
      if (toAdd.length === 0) return;

      const emptyOnes = cur.inconsistencies.filter((i) => !i.Description?.trim());
      await Promise.all(
        emptyOnes.map((e) => deleteStrapiData('mt-5-s-inconsistencies', e.documentId).catch(() => null))
      );
      const emptyDocIds = new Set(emptyOnes.map((e) => e.documentId));

      setAnswers((prev) => {
        const c = prev[questionId];
        if (!c) return prev;
        const kept = c.inconsistencies.filter((i) => !emptyDocIds.has(i.documentId));
        return { ...prev, [questionId]: { ...c, inconsistencies: [...kept, ...toAdd] } };
      });
    }, [prevIncsMap, answers, card?.CopiedFromCard?.id, copiedFromCardId]);

    const saveAnswers = async () => {
      await Promise.all(
        Object.entries(answers).map(([questionId, state]) => {
          if (!state.documentId) return null;
          const qid = Number(questionId);
          const question = templateQuestions.find((q: any) => q.id === qid);
          const payload = {
            Question: qid,
            QuestionTitle: question?.Title || null,
            Answer: state.answer || null,
            Comment: state.comment || null,
          };
          return updateStrapiData('mt-5-s-card-answers', state.documentId, payload).catch(() => null);
        })
      );
    };

    const collectAnswers = (): PassAnswerPayloadMobile[] => {
      const result: PassAnswerPayloadMobile[] = [];
      Object.entries(answers).forEach(([questionId, state]) => {
        if (!state.answer && !state.comment && state.inconsistencies.length === 0) return;
        const qid = Number(questionId);
        const question = templateQuestions.find((q: any) => q.id === qid);
        result.push({
          Question: qid,
          QuestionTitle: question?.Title || null,
          Answer: state.answer || null,
          Comment: state.comment || null,
        });
      });
      return result;
    };

    useImperativeHandle(ref, () => ({ getAnswers: collectAnswers, saveAnswers }), [answers, card, templateQuestions]);

    if (templatesLoading || questionsLoading || initializing) {
      return (
        <FlexCol className='items-center justify-center py-20'>
          <Loading />
        </FlexCol>
      );
    }

    if (!template) {
      return (
        <FlexCol className='items-center justify-center py-20 gap-4'>
          <p className='text-sm text-center text-[#637381]'>
            Шаблон чек-листа не найден ({card?.TypeAudit || '—'} · {checklistType || '—'})
          </p>
        </FlexCol>
      );
    }

    if (Object.keys(sections).length === 0) {
      return (
        <FlexCol className='items-center justify-center py-20'>
          <p className='text-sm text-[#637381]'>В шаблоне нет вопросов</p>
        </FlexCol>
      );
    }

    return (
      <FlexCol className='gap-6'>
        {copiedThisLoad && (
          <div className='bg-[#FFE4E6] text-[#1C252E] text-sm text-center py-2 px-4 rounded'>
            Результаты скопированы{' '}
            {copiedFromCardId && onOpenPrevAudit ? (
              <button
                type='button'
                onClick={() => onOpenPrevAudit(copiedFromCardId)}
                className='underline text-[#012169]'
              >
                из прошлого аудита
              </button>
            ) : (
              <span className='underline'>из прошлого аудита</span>
            )}
          </div>
        )}

        {Object.entries(sections).map(([sectionName, list]) => (
          <FlexCol key={sectionName} className='gap-5'>
            <FlexRow className='items-center justify-between border-b pb-2'>
              <h3 className='text-sm font-medium text-[#637381]'>{sectionName}</h3>
              <span className='text-sm text-[#637381]'>{calcPercent(list, answers)}%</span>
            </FlexRow>
            {list.map((q: any) => {
              const state = answers[q.id];
              if (!state) return null;
              const allowedAnswers: string[] =
                Array.isArray(q.AllowedAnswers) && q.AllowedAnswers.length > 0 ? q.AllowedAnswers : ANSWER_OPTIONS;
              const { text, hint: questionHint } = splitQuestionTitle(q.Title);
              return (
                <FlexCol key={q.id} id={`q-${q.id}`} className='gap-3 pb-4 border-b last:border-b-0'>
                  <div className='text-sm text-black'>
                    <span className='whitespace-pre-line'>{text}</span>
                    {questionHint && (
                      <button
                        type='button'
                        onClick={() => setHint(questionHint)}
                        className='inline-flex align-middle ml-1 text-[#637381]'
                        title='Подсказка'
                      >
                        <InformationCircleIcon className='w-4 h-4' />
                      </button>
                    )}
                  </div>

                  <FlexCol className='gap-2'>
                    {allowedAnswers.map((opt) => (
                      <label key={opt} className={`flex items-center gap-2 select-none ${readOnly ? '' : 'cursor-pointer'}`}>
                        <input
                          type='radio'
                          name={`q-${card?.id}-${q.id}`}
                          checked={state.answer === opt}
                          onChange={() => setAnswer(q.id, opt)}
                          disabled={readOnly}
                          className='w-4 h-4 accent-bluedark'
                        />
                        <span className='text-sm text-[#1C252E]'>{opt}</span>
                      </label>
                    ))}
                  </FlexCol>

                  <PrevAuditInfoMobile
                    answer={prevAnswersMap[q.id]?.answer}
                    inconsistencies={prevIncsMap[q.id] || []}
                  />

                  {!readOnly && (
                    <TGButton
                      className='w-full'
                      type='button'
                      icon={<PlusCircleIcon width={20} height={20} />}
                      onClick={() => handleAddInconsistency(q.id)}
                    >
                      Добавить несоответствие
                    </TGButton>
                  )}

                  {state.inconsistencies.map((inc) => (
                    <Inconsistency5SItemMobile
                      key={inc.documentId}
                      item={inc}
                      onUpdated={(documentId, description) => handleIncUpdated(q.id, documentId, description)}
                      showCopyPrev={!readOnly && !!(card?.CopiedFromCard?.id ?? copiedFromCardId) && !incsCopied[q.id]}
                      onCopyPrev={() => handleCopyPrevInconsistencies(q.id)}
                      readOnly={readOnly}
                    />
                  ))}
                </FlexCol>
              );
            })}
          </FlexCol>
        ))}

        <Modal
          zIndex={130}
          open={!!hint}
          toggleDialog={() => setHint(null)}
          modalClassName='w-[85vw] mx-auto min-h-0 sm:max-w-[420px] !pt-6'
          hideXMarkIcon
          closeOnBackdrop
        >
          <div className='px-6 pb-4 grow text-sm text-[#1C252E] [&_p]:m-0'>
            <ReactMarkdown remarkPlugins={[remarkBreaks, remarkGfm]} rehypePlugins={[rehypeSanitize]}>
              {hint || ''}
            </ReactMarkdown>
          </div>
          <div className='px-6'>
            <button
              type='button'
              className='w-full py-3 rounded-md bg-darkblue text-white'
              onClick={() => setHint(null)}
            >
              Закрыть
            </button>
          </div>
        </Modal>
      </FlexCol>
    );
  }
);

PassAudit5SMobile.displayName = 'PassAudit5SMobile';

export default PassAudit5SMobile;
